<script setup lang="ts">
  defineOptions({
    name: 'CompleteSignup',
  })

  // Vue core imports
  import type { PropType } from 'vue'

  import { computed, ref, watch } from 'vue'

  // External library imports
  import { useI18n } from 'vue-i18n'

  import * as yup from 'yup'

  import { Lock01Icon, Mail01Icon, User01Icon } from '@gohighlevel/ghl-icons/24/outline'

  // Internal imports
  import CPButton from '@ui/components/CPButton.vue'

  import CPForm from '@ui/components/CPForm.vue'

  import CPFormItem from '@ui/components/CPFormItem.vue'

  import CPInput from '@ui/components/CPInput.vue'

  import CPText from '@ui/components/CPText.vue'

  import { createElementId } from '../../../core-layer/utils'

  import { useAuth } from '../../composables/useAuth'

  import { LoginEvents, Screens } from '../../types/auth'

  import { getErrorMessage, SANITIZE_REGEX_PATTERN, validatePassword, validateText } from '../../utils'

  // Props definitions
  const props = defineProps({
    email: {
      type: String,
      required: true,
    },
    otp: {
      type: Number,
      required: true,
    },
    otpToken: {
      type: String,
      default: '',
    },
    referrerUser: {
      type: Object as PropType<{
        email: string
        name: string
        _id?: string
      }>,
      default: null,
      required: false,
    },
  })

  // Emits definitions
  const emit = defineEmits<{
    (e: 'changeScreen', value: any): void
    (e: 'loginCallback', value: any): void
  }>()

  // Refs and reactive variables
  const usernameError = ref('')

  const passwordError = ref('')

  const loading = ref(false)

  const errorMessage = ref()

  const formValue = ref({
    email: props.email,
    password: '',
    username: '',
  })

  const formRef = ref()

  const { t: $t } = useI18n()

  // Validation rules (reused from SignUp patterns)
  const rules = {
    username: {
      required: true,
      validator: (_: any, value: string) => {
        usernameError.value = ''

        if (!value) {
          usernameError.value = $t('auth.validation.fullNameRequired')
          return false
        }

        try {
          const result = validateText(value, $t)
          if (result instanceof Error) throw result
          return true
        } catch {
          usernameError.value = $t('auth.validation.fullNameInvalid')
          return false
        }
      },
      message: $t('auth.validation.nameRequired'),
      trigger: ['input'],
    },
    password: {
      required: true,
      min: 8,
      validator: (rule: any, value: string) => {
        passwordError.value = ''
        const result = validatePassword(rule, value, $t)
        if (result !== true) {
          passwordError.value = result.message
          return false
        }
        return true
      },
      trigger: ['input', 'blur'],
    },
  }

  // Computed properties
  const isFormValid = computed(() => {
    return (
      formValue.value.email &&
      formValue.value.username &&
      formValue.value.password &&
      formValue.value.password.length >= 8 &&
      !usernameError.value &&
      !passwordError.value
    )
  })

  const handleCompleteSignup = async (formRef: any) => {
    const isValidated = await validateForm(formRef)
    if (!isValidated) {
      return false
    }

    if (!formValue.value?.email || !formValue.value?.username || !formValue.value?.password) {
      console.error('Form values are undefined after validation')
      loading.value = false
      return
    }

    try {
      loading.value = true
      errorMessage.value = null

      const result = await useAuth().signUp({
        email: formValue.value.email.trim(),
        password: formValue.value.password.trim(),
        username: formValue.value.username.trim(),
        otp: props.otp,
        otpToken: props.otpToken,
        redirectUrl: window.location.search.match(/redirectUrl=([^&]*)/)?.[1] || '',
        referredBy: props.referrerUser?._id,
        signupPageUrl: window.location.href,
      })

      const data = 'data' in result ? result.data : result

      if (data && 'token' in data && data.token) {
        emit('loginCallback', {
          eventType: LoginEvents.LOGIN_SUCCESSFUL,
          ...data,
        })
      }
      loading.value = false
    } catch (error: any) {
      errorMessage.value = getErrorMessage(error)
      loading.value = false
    }
  }

  const onFormValueChange = () => {
    errorMessage.value = null
  }

  watch(
    () => formValue?.value?.password,
    value => {
      if (!value) {
        passwordError.value = $t('auth.validation.passwordFieldRequired')
        return
      }
      const result = validatePassword(null, value, $t)
      if (result === true) {
        passwordError.value = ''
      } else {
        passwordError.value = result.message
      }
    }
  )

  // User-defined functions
  async function validateForm(formRef: any): Promise<boolean> {
    try {
      await formRef.getForm().validate()
      return true
    } catch (e) {
      console.error('error', e)
      return false
    }
  }
</script>

<template>
  <div>
    <CPForm
      :id="createElementId('complete-signup', 'modal', 'signup-form')"
      ref="formRef"
      class="flex flex-col items-center"
      :model="formValue"
      :rules="rules"
      @keyup.enter="handleCompleteSignup(formRef)"
    >
      <CPFormItem class="w-full" path="username" :label="$t('auth.fullName')" required>
        <CPInput
          :id="createElementId('complete-signup', 'modal', 'username-input')"
          v-model="formValue.username"
          size="lg"
          :maxlength="50"
          :placeholder="$t('auth.fullNamePlaceholder')"
          @input="onFormValueChange"
        >
          <template #prefix>
            <User01Icon class="mr-2 h-5 w-5 pr-1"></User01Icon>
          </template>
        </CPInput>
        <template v-if="usernameError" #feedback>
          <CPText size="sm" class="text-danger">
            {{ usernameError }}
          </CPText>
        </template>
      </CPFormItem>

      <CPFormItem class="w-full" path="email" :label="$t('auth.email')" required>
        <CPInput
          :id="createElementId('complete-signup', 'modal', 'email-input')"
          v-model="formValue.email"
          size="lg"
          :placeholder="$t('auth.emailPlaceholder')"
          disabled
          @input="onFormValueChange"
        >
          <template #prefix>
            <Mail01Icon class="mr-2 h-5 w-5 pr-1"></Mail01Icon>
          </template>
        </CPInput>
      </CPFormItem>

      <CPFormItem class="w-full" path="password" :label="$t('auth.password')" required>
        <CPInput
          :id="createElementId('complete-signup', 'modal', 'password-input')"
          v-model="formValue.password"
          size="lg"
          type="password"
          :placeholder="$t('auth.passwordPlaceholder')"
          show-password-on="click"
          @input="onFormValueChange"
        >
          <template #prefix>
            <Lock01Icon class="mr-2 h-5 w-5 pr-1"></Lock01Icon>
          </template>
        </CPInput>
        <template #feedback>
          <CPText
            :id="createElementId('complete-signup', 'modal', 'password-field-feedback')"
            size="sm"
            :class="passwordError ? 'text-danger' : 'text-clientportal-font-secondary'"
          >
            {{ passwordError || $t('auth.messages.createStrongPassword') }}
          </CPText>
        </template>
      </CPFormItem>

      <div v-if="errorMessage" class="text-danger w-full">
        <CPText size="sm">{{ errorMessage }}</CPText>
      </div>

      <CPButton
        :id="createElementId('complete-signup', 'modal', 'signup-button')"
        class="mt-4 w-full rounded-xl shadow-sm"
        type="primary"
        size="lg"
        :loading="loading"
        :disabled="loading || !isFormValid"
        @click="handleCompleteSignup(formRef)"
      >
        {{ $t('auth.signUp') }}
      </CPButton>

      <div class="text-secondary mt-4 flex w-full justify-center">
        <CPButton
          :id="createElementId('complete-signup', 'modal', 'back-button')"
          type="text"
          color="secondary"
          size="xs"
          class="cursor-pointer hover:underline"
          @click="
            emit('changeScreen', {
              page: Screens.LOGIN_WITH_SECURE_CODE,
              email: formValue.email,
            })
          "
        >
          <CPText size="md" weight="semibold" html-tag="span">
            {{ $t('auth.back') }}
          </CPText>
        </CPButton>
      </div>
    </CPForm>
  </div>
</template>

<style scoped>
  :deep(.hr-input--disabled) {
    background-color: var(--cp-surface-500) !important;
    cursor: not-allowed !important;
  }

  :deep(.hr-input--disabled input) {
    color: var(--cp-text-disabled) !important;
    -webkit-text-fill-color: var(--cp-text-disabled) !important;
  }
</style>
